<template>
  <el-container class="app-layout">
    <component
      :is="DevToolToggle"
      v-if="dev && DevToolToggle"
      @toggle="toggleDevTool"
    />

    <el-header class="app-header" v-if="user">
      <el-row
        justify="space-between"
        align="middle"
        style="height: 100%; padding: 0 1rem"
      >
        <el-col :span="16">
          <h3 v-if="issue" class="issueTitle" :title="issue.title">
            {{ $filters.truncate(issue.title, 60) }}
          </h3>
          <span v-else></span>
        </el-col>

        <el-col :span="8" style="text-align: right">
          <el-menu
            ref="menu"
            mode="horizontal"
            :vue-router="false"
            :default-active="activeMenuItem"
            @select="handleSelect"
            style="
              background: transparent;
              border: none;
              --el-menu-text-color: var(--el-color-white);
              --el-menu-hover-text-color: var(--el-color-white);
              --el-menu-active-color: var(--el-color-white);
              --el-menu-bg-color: transparent;
              --el-menu-hover-bg-color: var(--el-color-primary-light-7);
              --el-menu-active-bg-color: var(--el-color-primary-light-7);
              color: var(--el-color-white);
            "
          >
            <el-menu-item index="issues">
              <el-icon><i-ep-opportunity /></el-icon>
              Opportunities
            </el-menu-item>

            <el-sub-menu index="user-menu">
              <template #title>
                <el-icon><i-ep-user /></el-icon>
                <span class="user-menu-label">
                  {{ user.id.split('@')[0] }} / {{ user.organisation.name }}
                </span>
              </template>
              <el-menu-item index="users">Users</el-menu-item>
              <el-menu-item index="logout" @click="onLogoutClicked">
                Logout
              </el-menu-item>
            </el-sub-menu>
          </el-menu>
        </el-col>
      </el-row>
    </el-header>

    <el-main>
      <router-view />
      <component :is="DevFetchTool" v-if="dev && showDevTool && DevFetchTool" />
    </el-main>
  </el-container>
</template>

<script setup lang="ts">
import '../assets/index.scss'
import { isEmpty } from 'lodash-es'
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  defineAsyncComponent,
} from 'vue'
import { useRouter } from 'vue-router'
import { useVendorStore } from './store/index'
import { useAuthApiStore } from '@/src/shared/auth/authApiStore'
import { useAuthOrchestrator } from '@/src/shared/auth/useAuthOrchestrator'

// Conditionally import dev components only in DEV environment
const DevFetchTool = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/src/components/DevFetchTool.vue'))
  : null

const DevToolToggle = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/src/components/DevToolToggle.vue'))
  : null

const store = useVendorStore()
const router = useRouter()
const authApiStore = useAuthApiStore()
const showDevTool = ref(false)
const dev = import.meta.env.DEV

// Set app type immediately (before orchestrator) for clarity
authApiStore.setAppType('vendor')

const issue = computed(() => store.currentIssue)
const user = computed(() => {
  return !isEmpty(store.user) ? store.user : null
})

// Add a computed property to determine the active menu item based on the current route
const activeMenuItem = computed(() => {
  const currentRouteName = router.currentRoute.value.name
  if (
    currentRouteName === 'issues' ||
    currentRouteName === 'issueHome' ||
    currentRouteName?.toString().startsWith('issue')
  ) {
    return 'issues'
  } else if (
    currentRouteName === 'users' ||
    currentRouteName === 'user' ||
    currentRouteName === 'addUser'
  ) {
    return 'users'
  }
  return ''
})

// Set up authentication orchestration AFTER app type is set
const { stopAuthWatcher } = useAuthOrchestrator({
  verifyUser: () => store.verify(),
})

onBeforeMount(() => {
  const userId = authApiStore.currentUserId
  const token = authApiStore.accessToken
  if (userId && token) {
    authApiStore.handleLoginSuccess(userId, token)
    // Only redirect automatically if actually on the login route
    // and no explicit redirectFrom was specified.
    if (
      router.currentRoute.value.name === 'Login' &&
      !router.currentRoute.value.query.redirectFrom
    ) {
      void router.push({ name: 'issues' })
    }
    // Removed previous path === '/' check which caused deep-link refresh
    // to be overwritten before the target route resolved.
  }
})

onBeforeUnmount(() => {
  stopAuthWatcher()
})

function toggleDevTool() {
  showDevTool.value = !showDevTool.value
}

function handleSelect(index: string) {
  if (index === 'logout') {
    // Don't navigate - the click handler will handle logout
    return
  }

  if (index) {
    void router.push({ name: index })
  }
}

async function onLogoutClicked() {
  if (confirm('You have logged out. We will now redirect you to login page')) {
    await authApiStore.logout()
    await router.push('/login')
  }
}
</script>

<style>
.el-header.app-header {
  padding: 0;
  background: var(--el-color-primary);
  color: var(--el-color-white);
}

.issueTitle {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--el-color-white);
}

.user-menu-label {
  display: inline-block;
  max-width: 24ch;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.clickable {
  cursor: pointer;
}

p {
  white-space: pre-line;
}
</style>
